import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Pill } from "@/dashboard/ui";

export default function Analytics() {
  const [s, setS] = useState(null);
  const [appt, setAppt] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setS(r.data)).catch(() => setS({}));
    api.get("/appointments/summary").then((r) => setAppt(r.data)).catch(() => setAppt(null));
  }, []);

  if (!s) return <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>;

  const statusRows = Object.entries(s.leads_by_status || {});
  const statusMax = Math.max(1, ...statusRows.map(([, v]) => v));
  const dayRows = s.leads_by_day || [];
  const dayMax = Math.max(1, ...dayRows.map((d) => d.count));

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Analytics" sub="Built only from your saved records. Call analytics appear once telephony is connected." />

      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: "Open tasks", value: s.tasks_open },
          { label: "Completed tasks", value: s.tasks_done },
          { label: "Content items", value: Object.values(s.drafts_by_section || {}).reduce((a, b) => a + b, 0) },
          { label: "Campaigns", value: s.campaigns },
        ].map((c) => (
          <Card key={c.label} className="p-5">
            <p className="text-3xl font-extrabold tracking-tight text-[#111111]">{c.value}</p>
            <p className="mt-1 text-xs font-medium text-neutral-500">{c.label}</p>
          </Card>
        ))}
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <Card className="p-6" data-testid="analytics-leads-by-status">
          <p className="mb-5 text-xs font-bold uppercase tracking-wider text-neutral-500">Leads by status</p>
          <div className="space-y-3.5">
            {statusRows.map(([label, value]) => (
              <div key={label}>
                <div className="mb-1 flex justify-between text-xs">
                  <span className="font-medium text-neutral-600">{label}</span>
                  <span className="font-bold text-[#111111]">{value}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                  <div className="h-full rounded-full bg-[#111111] transition-all" style={{ width: `${(value / statusMax) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="analytics-leads-by-day">
          <p className="mb-5 text-xs font-bold uppercase tracking-wider text-neutral-500">New leads — last 7 days</p>
          <div className="flex h-36 items-end gap-2" data-testid="leads-by-day-chart">
            {dayRows.map((d) => (
              <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                <div
                  className="w-full rounded-t-lg bg-[#111111]"
                  data-testid={`leads-chart-bar-${d.date}`}
                  style={{ height: `${Math.max(d.count > 0 ? 12 : 3, (d.count / dayMax) * 100)}%`, opacity: d.count ? 0.4 + (d.count / dayMax) * 0.6 : 0.15 }}
                  title={`${d.date}: ${d.count} new lead${d.count === 1 ? "" : "s"}`}
                />
                <span className="text-[10px] font-medium text-neutral-400">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
          {dayRows.every((d) => d.count === 0) && (
            <p className="mt-3 text-xs text-neutral-400" data-testid="leads-chart-zero-state">
              No new leads in the last 7 days — leads you add will appear here on their creation day.
            </p>
          )}
        </Card>
      </div>

      {appt && (
        <Card className="p-6" data-testid="analytics-appointments">
          <p className="mb-4 text-xs font-bold uppercase tracking-wider text-neutral-500">Appointments</p>
          <div className="flex flex-wrap gap-2.5">
            <Pill tone="black"><span data-testid="analytics-appt-upcoming">Upcoming: {appt.upcoming_confirmed}</span></Pill>
            {Object.entries(appt.by_status || {}).map(([k, v]) => (
              <Pill key={k} tone="outline">{k}: {v}</Pill>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-5 flex items-center justify-between p-6" data-testid="analytics-calls-card">
        <div>
          <p className="text-sm font-bold text-[#111111]">Call analytics</p>
          <p className="mt-1 text-xs text-neutral-500">Duration, outcomes and conversion from calls — available after the LiveKit Agents / Pipecat integration.</p>
        </div>
        <Pill tone="outline">Not connected</Pill>
      </Card>
    </div>
  );
}
